import {
  CSSProperties,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import cn from 'classnames'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { Modal } from '@lib/components/Modal'

import {
  DROPDOWN_DEFAULT_ALIGN,
  DROPDOWN_DEFAULT_MOBILE_BREAKPOINT,
  DROPDOWN_DEFAULT_OFFSET,
  DROPDOWN_DEFAULT_TITLE_KEY,
} from './constants'
import { DropdownContext } from './context'
import styles from './styles.module.scss'
import {
  DropdownAlign,
  DropdownProps,
  DropdownRenderContentArgs,
  DropdownRenderToggleArgs,
} from './types'

const useMediaQuery = (query: string) => {
  const getMatches = useCallback(() => {
    if (typeof window === 'undefined') {
      return false
    }

    return window.matchMedia(query).matches
  }, [query])

  const [matches, setMatches] = useState<boolean>(getMatches)

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    const mediaQuery = window.matchMedia(query)
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches)
    }

    setMatches(mediaQuery.matches)

    mediaQuery.addEventListener('change', handleChange)

    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [query])

  return matches
}

const getTransformByAlign = (align: DropdownAlign) => {
  switch (align) {
    case 'center':
      return 'translateX(-50%)'
    case 'end':
      return 'translateX(-100%)'
    default:
      return undefined
  }
}

export const Dropdown = ({
  renderToggle,
  renderContent,
  isOpen,
  defaultOpen = false,
  onOpenChange,
  align = DROPDOWN_DEFAULT_ALIGN,
  offset = DROPDOWN_DEFAULT_OFFSET,
  className,
  popoverClassName,
  mobileBreakpoint = DROPDOWN_DEFAULT_MOBILE_BREAKPOINT,
  useModalOnMobile = true,
  modalTitle,
  modalProps,
  containerId,
}: DropdownProps) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen)
  const open = isOpen ?? internalOpen

  const triggerRef = useRef<HTMLElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [positionStyle, setPositionStyle] = useState<CSSProperties | null>(
    null
  )
  const contentId = useId()

  const isControlled = isOpen !== undefined

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setInternalOpen(next)
      }
      onOpenChange?.(next)
    },
    [isControlled, onOpenChange]
  )

  const toggle = useCallback(
    (value?: boolean) => {
      setOpen(value ?? !open)
    },
    [open, setOpen]
  )

  const close = useCallback(() => setOpen(false), [setOpen])

  const { t } = useTranslation()
  const resolvedTitle = useMemo(
    () =>
      modalTitle ??
      t(DROPDOWN_DEFAULT_TITLE_KEY),
    [modalTitle, t]
  )

  const isMobile = useMediaQuery(`(max-width: ${mobileBreakpoint}px)`)

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) {
      return
    }

    const rect = triggerRef.current.getBoundingClientRect()
    const style: CSSProperties = {
      top: rect.bottom + window.scrollY + offset,
      minWidth: rect.width,
    }

    if (align === 'end') {
      style.left = rect.right + window.scrollX
      style.transform = getTransformByAlign('end')
    } else if (align === 'center') {
      style.left = rect.left + rect.width / 2 + window.scrollX
      style.transform = getTransformByAlign('center')
    } else {
      style.left = rect.left + window.scrollX
    }

    setPositionStyle(style)
  }, [align, offset])

  useLayoutEffect(() => {
    if (!open || isMobile || !triggerRef.current) {
      return
    }

    updatePosition()

    const handleScroll = () => updatePosition()
    const handleResize = () => updatePosition()

    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', handleResize)

    let resizeObserver: ResizeObserver | undefined
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => updatePosition())
      resizeObserver.observe(triggerRef.current)
    }

    return () => {
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', handleResize)
      resizeObserver?.disconnect()
    }
  }, [open, isMobile, updatePosition])

  useEffect(() => {
    if (!open || isMobile) {
      return
    }

    const handleClick = (event: MouseEvent) => {
      if (
        triggerRef.current?.contains(event.target as Node) ||
        contentRef.current?.contains(event.target as Node)
      ) {
        return
      }

      close()
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close()
      }
    }

    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, isMobile, close])

  const portalNode = useMemo(() => {
    if (typeof document === 'undefined') {
      return null
    }

    if (containerId) {
      return document.getElementById(containerId)
    }

    return document.body
  }, [containerId])

  const renderToggleArgs: DropdownRenderToggleArgs = useMemo(
    () => ({
      ref: triggerRef,
      isOpen: open,
      toggle,
      id: contentId,
    }),
    [open, toggle, contentId]
  )

  const renderContentArgs: DropdownRenderContentArgs = useMemo(
    () => ({ close }),
    [close]
  )

  const dropdownContent = (
    <DropdownContext.Provider value={{ close }}>
      {renderContent(renderContentArgs)}
    </DropdownContext.Provider>
  )

  return (
    <div className={cn(styles.dropdown, className)}>
      {renderToggle(renderToggleArgs)}
      {!isMobile && open && portalNode && positionStyle
        ? createPortal(
          <div
            ref={contentRef}
            className={cn(styles.popover, popoverClassName)}
            style={positionStyle}
            role="menu"
            id={contentId}
          >
            {dropdownContent}
          </div>,
          portalNode
        )
        : null}
      {isMobile && useModalOnMobile ? (
        <Modal
          isOpen={open}
          onClose={close}
          title={resolvedTitle}
          showBottomCloseButton={false}
          {...modalProps}
        >
          <div className={styles.drawerContent}>{dropdownContent}</div>
        </Modal>
      ) : null}
    </div>
  )
}
